import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import axios from '../utils/axiosConfig';

const STATUS_LABEL = {
  '010': 'Completed', '011': 'Timed Out', '111': 'Web Proctoring Aborted', '112': 'System Aborted', '113': 'Image Proctoring Aborted', '114': 'Speaker Proctoring Aborted',
  '001': 'In Progress', '000': 'Not Started', '100': 'Expired',
};

const STATUS_COLOR = {
  '010': { background: '#e8f5e9', color: '#2e7d32' },
  '011': { background: '#fff8e1', color: '#f57f17' },
  '111': { background: '#fce4ec', color: '#c62828' },
  '112': { background: '#fce4ec', color: '#c62828' },
  '113': { background: '#fce4ec', color: '#c62828' },
  '114': { background: '#fce4ec', color: '#c62828' },
  '001': { background: '#e3f2fd', color: '#1565c0' },
  '000': { background: '#f5f5f5', color: '#757575' },
  '100': { background: '#fff3e0', color: '#e65100' },
};

const s = {
  wrapper: { paddingBottom: 16 },
  tabRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },

  // Levels list
  levelsBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' },
  levelRow: { padding: 0 },
  levelRowInner: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px',
  },
  levelBadge: {
    background: '#e0f7fa', color: '#00838f',
    borderRadius: 5, padding: '2px 10px',
    fontSize: 11, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap',
  },
  levelName: { flex: 1, fontSize: 12, fontWeight: 600, color: '#333' },
  messageText: { fontSize: 11, color: '#888', fontStyle: 'italic', flexShrink: 0 },

  // Table
  tableWrap: { overflowX: 'auto', padding: '0 16px 12px' },
  table: {
    width: '100%', borderCollapse: 'collapse',
    fontSize: 12, tableLayout: 'fixed',
  },
  colTest: { width: '40%' },
  colStatus: { width: '18%' },
  colResult: { width: '18%' },
  colDate: { width: '24%' },
  th: {
    padding: '9px 10px', textAlign: 'left', fontWeight: 600,
    fontSize: 11, color: '#888', letterSpacing: '0.4px',
    textTransform: 'uppercase',
    background: '#f5f5f5', borderBottom: '2px solid #e8e8e8',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '9px 10px', color: '#444',
    borderBottom: '1px solid #f5f5f5',
    verticalAlign: 'middle',
  },
  tdCenter: {
    padding: '9px 10px', color: '#444',
    borderBottom: '1px solid #f5f5f5',
    verticalAlign: 'middle', textAlign: 'center',
  },
  tdDate: {
    padding: '9px 10px', color: '#777',
    borderBottom: '1px solid #f5f5f5',
    verticalAlign: 'middle', fontSize: 11,
    whiteSpace: 'nowrap',
  },
  badge: (code) => ({
    display: 'inline-block', borderRadius: 4,
    padding: '3px 10px', fontSize: 11, fontWeight: 600,
    whiteSpace: 'nowrap',
    ...(STATUS_COLOR[code] || { background: '#f5f5f5', color: '#555' }),
  }),
  resultBadge: {
    display: 'inline-block', borderRadius: 4,
    padding: '3px 10px', fontSize: 11, fontWeight: 600,
    whiteSpace: 'nowrap',
    background: '#e8f5e9', color: '#2e7d32',
  },
  testName: {
    overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', display: 'block',
  },
  noTests: { padding: '12px 16px', fontSize: 12, color: '#bbb', fontStyle: 'italic' },
  loading: { padding: 16, color: '#aaa', fontSize: 13 },
};

export default function WorkflowHistoryPanel() {
  const [workflows, setWorkflows] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/dashboard/workflow-history')
      .then(res => {
        if (!res.data?.success) return;
        const rows = res.data.history || [];

        // Group by workflow_id, preserving order
        const map = {};
        const order = [];
        rows.forEach(row => {
          const id = row.workflow_id;
          if (!map[id]) { map[id] = { name: row.workflow_name || 'Workflow', levels: [] }; order.push(id); }
          map[id].levels.push(row);
        });
        setWorkflows(order.map(id => map[id]));
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={s.loading}>Loading workflow history…</div>;
  if (!workflows.length) return null;

  const active = workflows[activeIdx] || workflows[0];

  return (
    <div style={s.wrapper}>
      {/* ── Workflow tabs ── */}
      <div style={s.tabRow}>
        {workflows.map((wf, i) => (
          <Button key={i} variant='outlined'
            color={activeIdx === i ? 'primary' : 'default'}
            style={{ background: activeIdx === i ? 'rgba(111, 41, 129, 0.13)' : '' }}
            onClick={() => setActiveIdx(i)}
          >
            {wf.name}
          </Button>
        ))}
      </div>

      {/* ── Levels table for selected workflow (latest attempt only) ── */}
      {active && (
        <div style={s.levelsBox}>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <colgroup>
                <col style={s.colTest} />
                <col style={s.colStatus} />
                <col style={s.colResult} />
                <col style={s.colDate} />
              </colgroup>
              <thead>
                <tr>
                  <th style={s.th}>Test</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Test Status</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Result</th>
                  <th style={s.th}>Completed</th>
                </tr>
              </thead>
              <tbody>
                {active.levels.map((lvl, i) => {
                  const tests = Array.isArray(lvl.tests) ? lvl.tests : [];
                  return (
                    <React.Fragment key={i}>
                      <tr style={{ background: '#f0f7f8' }}>
                        <td colSpan={4} style={s.levelRow}>
                          <div style={s.levelRowInner}>
                            <span style={s.levelBadge}>{`Level ${i + 1}`}</span>
                            <span style={s.levelName}>{lvl.level_name || '—'}</span>
                            {lvl.message && <span style={s.messageText}>{lvl.message}</span>}
                          </div>
                        </td>
                      </tr>
                      {tests.length === 0 ? (
                        <tr><td colSpan={4} style={s.noTests}>No tests recorded</td></tr>
                      ) : (
                        tests.map((t, j) => (
                          <tr key={j} style={{ background: j % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={s.td}><span style={s.testName} title={t.test_name}>{t.test_name}</span></td>
                            <td style={s.tdCenter}><span style={s.badge(t.status)}>{STATUS_LABEL[t.status] || t.status || '—'}</span></td>
                            <td style={s.tdCenter}><span style={s.resultBadge}>{t.result_status || '—'}</span></td>
                            <td style={s.tdDate}>{t.completed_at || '—'}</td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
